import { Link, useParams, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileSticky from "../components/MobileSticky";
import QuoteForm from "../components/QuoteForm";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { SUBURBS, SERVICES, COMPANY } from "../data/site";

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = SUBURBS.find((s) => s.slug === slug);
  if (!suburb) return <Navigate to="/" replace />;

  const hero = suburb.hero || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";
  const content = suburb.content;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/${suburb.slug}`;

  // Default service links if this suburb doesn't have a custom `services` list
  const serviceLinks = (content && content.services) || SERVICES.map((s) => ({ label: s.name, slug: s.slug }));

  return (
    <div data-testid={`page-suburb-${suburb.slug}`}>
      <Seo
        title={`Painters ${suburb.name} | Connolly's Painting Solutions`}
        description={`Painters ${suburb.name} — Connolly's Painting Solutions repaints homes and businesses across ${suburb.name} and surrounds on the Sunshine Coast. ${suburb.blurb} Get a free quote today.`}
        path={`/${suburb.slug}`}
        image={hero}
        schema={[
          localBusinessSchema(COMPANY),
          breadcrumbSchema([
            { name: "Home", url: `${origin}/` },
            { name: "Service Areas", url: `${origin}/suburbs` },
            { name: `Painters ${suburb.name}`, url: pageUrl },
          ]),
        ]}
      />
      <Header />

      {/* HERO IMAGE */}
      <section className="relative bg-[#0B1426] text-white">
        <div className="absolute inset-0">
          <img src={hero} alt={suburb.name} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1426]/30 via-[#0B1426]/50 to-[#0B1426]" />
        </div>
        <div className="relative container-cps pt-20 pb-28 md:pt-28 md:pb-40 max-w-4xl">
          <div className="overline">Sunshine Coast · QLD {suburb.postcode}</div>
          <h1 className="font-display text-5xl md:text-7xl mt-5 tracking-tight leading-[1.05]">
            Painters in <span style={{ color: "#C5A059" }}>{suburb.name}.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl">{suburb.blurb}</p>
        </div>
      </section>

      {/* LOCAL CONTENT — unique per suburb where available */}
      {content && (
        <section className="bg-white py-14 md:py-20" data-testid={`suburb-content-${suburb.slug}`}>
          <div className="container-cps grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-7">
              <div className="overline">Local painters, {suburb.name}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight leading-tight text-[#0B1426]">
                Painting services in {suburb.name}, done properly.
              </h2>
              <p className="mt-5 text-[#4F5B6A] leading-relaxed text-[15px] md:text-base">{content.intro}</p>
              {content.whyLocal && (
                <p className="mt-4 text-[#4F5B6A] leading-relaxed text-[15px] md:text-base">{content.whyLocal}</p>
              )}
            </div>
            <div className="lg:col-span-5">
              {content.areas && content.areas.length > 0 && (
                <div>
                  <div className="overline text-[#4F5B6A]">Areas we cover</div>
                  <div className="hairline-gold mt-3 w-10" />
                  <ul className="mt-4 space-y-2 text-[#0B1426] text-[15px]">
                    {content.areas.map((a) => (
                      <li key={a}>· {a}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-8">
                <div className="overline text-[#4F5B6A]">Painting services in {suburb.name}</div>
                <div className="hairline-gold mt-3 w-10" />
                <ul className="mt-4 space-y-2 text-[15px]">
                  {serviceLinks.map((sl) => (
                    <li key={sl.slug}>
                      <Link
                        to={`/${sl.slug}`}
                        className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
                        data-testid={`suburb-service-link-${sl.slug}`}
                      >
                        {sl.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* QUOTE FORM */}
      <section className="section bg-[#F9F8F6]" data-testid={`suburb-quote-${suburb.slug}`}>
        <div className="container-cps grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Start your journey</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight">
              Get a quote for your {suburb.name} property.
            </h2>
            <p className="mt-6 text-[#4F5B6A] leading-relaxed">
              Submit your details and we'll respond within one business day with a clear, fixed-price quote.
            </p>
            <div className="hairline-gold mt-10" />
            <div className="mt-6 space-y-3 text-sm text-[#4F5B6A]">
              <div>📞 <a href={`tel:${COMPANY.phoneTel}`} className="text-[#0B1426] hover:text-[#C5A059]" data-testid={`suburb-phone-${suburb.slug}`}>{COMPANY.phone}</a></div>
              <div>✉ <a href={`mailto:${COMPANY.email}`} className="text-[#0B1426] hover:text-[#C5A059]">{COMPANY.email}</a></div>
              <div>📍 Servicing all of {suburb.name} and surrounds</div>
            </div>
          </div>
          <div className="lg:col-span-7 bg-white p-8 md:p-12 border border-[#E5E3DB]">
            <QuoteForm sourcePage={`suburb-${suburb.slug}`} />
          </div>
        </div>
      </section>

      <Footer />
      <MobileSticky />
    </div>
  );
}
